import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Context } from 'src/app/shared/models/context';
import { Message, PrivilegeType, TableData } from 'src/app/shared/models/domain';
import { Voucher } from 'src/app/shared/models/finance';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import { ManageVoucherComponent } from '../manage-voucher/manage-voucher.component';
import * as XLSX from 'xlsx';
import { DomainService } from 'src/app/shared/services/domain.service';
import { DatePipe } from '@angular/common';


@Component({
  selector: 'app-voucher-list',
  templateUrl: './voucher-list.component.html',
  styleUrls: ['./voucher-list.component.scss']
})
export class VoucherListComponent implements OnInit {
  public tableData: TableData = { content: [], number: 1, size: 10, totalElements: 0 } as TableData;
  public pageSizes: number[] = [];
  public isLoading: boolean = true;
  public searchKeyword: string = "";
  public privilege: PrivilegeType;
  private pageDataKey: string = 't';
  public selectedFilter: string = '';
  public filters: string[] = [];
  public fromDate: Date;
  public toDate: Date;
  public filter: any;
  public context: Context;
  private subscription: any;


  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private domainService: DomainService,
    private dataService: DataService,
    private datePipe: DatePipe,
    private financeService: FinanceService,
  ) { }

  ngOnInit(): void {
    this.pageSizes = this.commonService.getDefaultPageSizes();
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.dataService.privilege().subscribe(privilege => { this.privilege = this.commonService.getPrivileges(privilege) });
    this.filters = this.domainService.getDateFiltersTypes();
    this.selectedFilter = this.filters[2] || 'Last Week';
    this.filter = {
      from: '',
      to: ''
    };
    this.fromDate = this.commonService.adjustDateByDays(-30);
    this.toDate = new Date();
    this.subscription = this.authService.getContext().subscribe(context => {
      this.context = context;
      if (context) {
        this.getVouchers();
      }
    });
  }

  private getVouchers(clear: boolean = true, pageNumber: number = 0) {
    if (clear) {
      this.isLoading = true;
      this.tableData.content = [];
    }
    const pageSize = Number(this.tableData.size);
    const fromDate = this.fromDate ? this.datePipe.transform(this.fromDate, 'dd-MM-yyyy') : undefined;
    const toDate = this.toDate ? this.datePipe.transform(this.toDate, 'dd-MM-yyyy') : undefined;
    this.financeService.getVouchers(pageNumber, pageSize, fromDate, toDate).subscribe({
      next: (tableData: TableData) => {
        this.tableData = {
          ...tableData,
          number: pageNumber + 1,
        };
        this.tableData.size = pageSize;
        this.tableData.content = this.tableData?.content?.sort((a, b) => a.id - b.id);
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    })
  }

  public upsertVoucher(voucher?: Voucher) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ManageVoucherComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.voucher = voucher;
    modalRef.componentInstance.passEntry.subscribe((response: Message) => {
      if (!response.failed) {
        this.getVouchers(false);
      }
    })
  }

  public loadTableData(e: any) {
    this.tableData.size = +this.tableData.size;
    this.tableData.number = 1;
    this.commonService.storePageData(this.pageDataKey, this.tableData.size, this.tableData.number);
    this.getVouchers(true);
  }

  public onPageChange(newPage: number): void {
    this.tableData.number = newPage;
    const pageNumber = newPage - 1;
    this.getVouchers(false, pageNumber);
  }

  public updateVoucherStatus(voucher: Voucher) {
    this.financeService.getVoucherStatus(voucher.id, 'POSTED').subscribe({
      next: (response: Message) => {
        if (response) {
          this.commonService.setToastr(200, response.text);
          this.getVouchers();
        } else {
          this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
        }
      }
    })
  }

  public toggleExpandRow(voucher: Voucher) {
    voucher.expanded = !voucher.expanded;
  }


  public downloadExcel(): void {
    const exportData: any[] = [];
    this.tableData.content.forEach(voucher => {
      exportData.push({
        'Voucher Date': new Date(voucher.date).toLocaleDateString(),
        'Voucher No': voucher.voucherNo,
        'Type': voucher.voucherType,
        'Total Debit': voucher.totalDebit,
        'Total Credit': voucher.totalCredit,
        'Status': voucher.voucherStatus
      });
      if (voucher.voucherEntryDtos && voucher.voucherEntryDtos.length > 0) {
        voucher.voucherEntryDtos.forEach(entry => {
          exportData.push({
            'Voucher Date': '',
            'Voucher No': '',
            'Type': `↳ ${entry.byAccount ? 'byAccount' : 'toAccount'}`,
            'Account Name': entry.accountName,
            'Particular': entry.particular,
            'Total Debit': entry.byAccount ? entry.amount : '',
            'Total Credit': entry.credit ? entry.amount : '',
            'Status': ''
          });
        });
      }
    });
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Vouchers': worksheet },
      SheetNames: ['Vouchers']
    };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    const fileName = `Vouchers_${new Date().getTime()}.xlsx`;
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  public filterAndFetchTransation(filter: string, element?: any) {
    const today = new Date();
    let fromDate: Date;
    let toDate: Date;
    const [startDate, endDate] = this.context.financialYearName?.split(' - ') ?? [];
    const financialYearStartDate = new Date(startDate);
    const financialYearEndDate = new Date(endDate);
    financialYearEndDate.setHours(23, 59, 59, 999);

    switch (filter) {
      case 'Today':
        fromDate = new Date(today.setHours(0, 0, 0, 0));
        toDate = new Date(today.setHours(23, 59, 59, 999));
        break;

      case 'Yesterday':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 1);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(fromDate);
        toDate.setHours(23, 59, 59, 999);
        break;

      case 'Last Week':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 7);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;

      case 'Custom':
        if (!this.fromDate || !this.toDate) {
          this.commonService.setToastr(400, 'Please select the date range!');
          return;
        }

        fromDate = new Date(this.fromDate);
        toDate = new Date(this.toDate);
        toDate.setHours(23, 59, 59, 999);
        if (fromDate < financialYearStartDate || toDate > financialYearEndDate) {
          const range = `${this.datePipe.transform(financialYearStartDate, 'dd MMM yyyy')} - ${this.datePipe.transform(financialYearEndDate, 'dd MMM yyyy')}`;
          this.commonService.setToastr(400, `Date range must be within financial year: ${range}`);
          return;
        }
        break;
      default:
        return;
    }
    element?.close();
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.getVouchers();
  }
}
